<template lang="pug">
    span(
        class="_lb",
        :class="{ _fo:focused, _filt:filtered, _da:disabled, _inv:showvalid && !valid, ['_le' + appearance]: appearance }",
        :style="{ width, height }",
        v-on="__fwd")
        span(v-if="filtered", class="_fldi")
            input(
                v-model="filter",
                class="_fldin",
                autocomplete="off",
                autocorrect="off",
                autocapitalize="off",
                @focus="__focus",
                @blur="__blur")
        ul(
            ref="_in",
            role="listbox",
            :tabindex="inert ? -1 : (tabindex || 0)",
            v-on="__on",
            @keydown="_keydown",
            @keyup="_keyup",
            @touchstart="_touchstart",
            @touchend="_touchend",
            @touchmove="_touchmove",
            @scroll="_scroll",
            @wheel="_wheel")
            template(v-for="opt in data")
                template(v-if="opt.child")
                    li(inert)
                        slot(v-bind="opt") {{opt.label}}
                    li(v-for="subopt in opt.child",
                        v-show="isFiltered(subopt)",
                        class="_ind",
                        role="option",
                        :data-value="getval(subopt)")
                        slot(v-bind="typeof subopt === 'object' ? subopt : {value:subopt}") {{typeof subopt === 'object' ? subopt.label : subopt}}
                li(v-else,
                    v-show="isFiltered(opt)",
                    role="option",
                    :data-value="getval(opt)")
                    slot(v-bind="typeof opt === 'object' ? opt : {value:opt}") {{typeof opt === 'object' ? opt.label : opt}}
            slot(name="bg")
</template>

<script>
    import entry from './ib-form-entry.vue';
    import $device from '../../global/device';
    import $util from '../../global/util';

    const selectAttr = 'aria-selected';


    /**
     * A list box component supporting single or multiple selection, with mobile behavior
     * identical to desktop behavior.
     *
     * Supported classes:
     * - `compact`: Vertically compact view.
     * - `comfortable`: Vertically expanded view.
     */
    export default {
        name: 'ib-listbox',

        mixins: [
            entry
        ],

        props: {
            /**
             * An array of objects, each of which is a list entry with a label and a value.
             * It is possible to nest subentries below an entry by using the `child` property.
             */
            data: {
                type: Array,
                default: () => []
            },
            /**
             * Value of currently selected entry, typically applied with `v-model`.
             */
            value: {
                type: [String, Number, Boolean, Array],
                default: undefined
            },
            /**
             * Member of data entry to emit as value. May be specified as a function.
             * By default, for object entries we emit the `value` member and for non-object entries we emit the entry.
             */
            emit: {
                type: [String, Number, Function],
                default: 'value'
            },
            /**
             * Allow selection of multiple entries; if true, emits an array.
             */
            multiple: Boolean,
            /**
             * Allow filtering of displayed entries via keyboard input.
             */
            filtered: Boolean,
            /**
             * Appearance modifier: `frameless` is supported.
             */
            appearance: String
        },

        data() {
            return {
                li: null,
                active: null,
                activeto: 0,
                button: false,
                changed: false,
                filter: null,
                observer: null,
                validity: null,
                option: [],
                selectedOptions: []
            };
        },

        computed: {
            getval() {
                const emit = this.emit;
                return typeof emit === 'function'
                    ? emit
                    : o => typeof o === 'object' ? o[emit] : o;
            },

            filterexp() {
                return this.filter
                    ? RegExp(this.filter.replace(/[\\^$.|?*+()[{]/g, '\\$&').replace(/\s+/g, '.+'), 'i')
                    : null;
            }
        },

        watch: {
            data(data) {
                // If we mutate data and value no longer corresponds to an available entry, emit null
                if (this.ivalue != null) {
                    for (let i = 0; i < data.length; i++) {
                        if (data[i].child) {
                            for (let j = 0; j < data[i].child.length; j++) {
                                if (this.getval(data[i].child[j]) == this.ivalue) {
                                    return;
                                }
                            }
                        } else {
                            if (this.getval(data[i]) == this.ivalue) {
                                return;
                            }
                        }
                    }
                    this._set(null);
                }
                this.$nextTick(() => {
                    this.option = [].slice.call(this.$refs._in.children);
                });
            }
        },

        mounted() {
            this.option = [].slice.call(this.$refs._in.children);
        },

        destroyed() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },

        methods: {
            /**
             * If the listbox is scrollable, scroll it so the specified values (or the currently selected values if not specified) are centered in view.
             */
            center(value) {
                this.$nextTick(() => {
                    let options;
                    if (value == null) {
                        options = this.selectedOptions;
                    } else {
                        if (!(value instanceof Array)) {
                            value = [value];
                        }
                        options = value.map(e => this.$refs._in.querySelector('[data-value=\'' + ('' + e).replace(/'/g, '\\\'') + '\']'));
                    }
                    $util.scrollIntoCenter(options, this.$refs._in);
                })
            },

            /** @private */
            _sync() {
                let value = this.ivalue;
                if (value == null || value === '') {
                    this._clear();
                } else {
                    if (!(value instanceof Array)) {
                        value = [value];
                    }
                    let ul = this.$refs._in;
                    let li = value.map(e => ul.querySelector('[data-value=\'' + ('' + e).replace(/'/g, '\\\'') + '\']'));
                    li.forEach(e => {
                        if (e && this.selectedOptions.indexOf(e) < 0) {
                            e.setAttribute(selectAttr, 'true');
                        }
                    });
                    this.selectedOptions.forEach(e => {
                        if (e && li.indexOf(e) < 0) {
                            e.removeAttribute(selectAttr);
                        }
                    });
                    this.selectedOptions = li.filter(e => e != null);
                    this.li = this.selectedOptions.length == 1 ? this.selectedOptions[0] : null;
                    $util.scrollIntoView(this.selectedOptions, this.$refs._in);
                }
                this.validate();
            },

            /** @private */
            _keydown(e) {
                if (!this.disabled && !this.inert) {
                    switch (e.which) {
                    case 33:  // PageUp
                        this._move(e, 'previousElementSibling', 6);
                        break;

                    case 34:  // PageDown
                        this._move(e, 'nextElementSibling', 6);
                        break;

                    case 38:  // ArrowUp
                        this._move(e, 'previousElementSibling', 1);
                        break;

                    case 40:  // ArrowDown
                        this._move(e, 'nextElementSibling', 1);
                        break;

                    case 65:  // A
                        if (($device.mac ? e.metaKey : e.ctrlKey) && this.multiple) {
                            this.selectedOptions = [];
                            for (let i = 0; i < this.option.length; i++) {
                                const li = this.option[i];
                                if ($util.isVisible(li) && !li.getAttribute('inert')) {
                                    this.selectedOptions.push(li);
                                    li.setAttribute(selectAttr, 'true');
                                }
                            }
                            this.changed = true;
                            this._enter();
                        }
                        break;

                    default:
                        return;
                    }
                    e.preventDefault();
                }
            },

            /** @private */
            _keyup(e) {
                this._enter();
            },

            /** @private */
            _mousedown(e) {
                if (!this.disabled) {
                    if (e.button == 0) {
                        const li = this.getOption(e.target);
                        if (li) {
                            if (this.multiple && e.shiftKey) {
                                this.selectRange(li);
                            } else {
                                this.select(li, !e.ctrlKey);
                            }
                            this.button = true;
                            $util.scrollIntoView(li, this.$refs._in);
                            const input = this.$refs._in;
                            if (input.setCapture) {
                                input.setCapture();
                            } else {
                                document.body.addEventListener('mouseup', this.onBodyUp);
                                document.documentElement.addEventListener('mouseout', this._bodyout);
                            }
                            input.addEventListener('mousemove', this._mousemove);
                        }
                    } else {
                        this.button = false;
                    }
                } else {
                    e.preventDefault();
                }
            },

            /** @private */
            _mouseup(e) {
                const input = this.$refs._in;
                if (input.releaseCapture) {
                    input.releaseCapture();
                }
                input.removeEventListener('mousemove', this._mousemove);
                this.button = false;
                this._enter();
            },

            /** @private */
            _mousemove(e) {
                if (this.button && $util.isInside(this.$refs._in, e.pageX, e.pageY)) {
                    const li = this.getOption(e.target);
                    if (li) {
                        if (this.multiple) {
                            this.selectRange(li);
                        } else {
                            this.select(li);
                        }
                        $util.scrollIntoView(li, this.$refs._in);
                    }
                }
            },

            /** @private */
            _bodyout(e) {
                if (e.target === document.documentElement) {
                    this._bodyup();
                }
            },

            /** @private */
            _bodyup() {
                document.body.removeEventListener('mouseup', this._bodyup);
                document.documentElement.removeEventListener('mouseout', this._bodyout);
                this.button = false;
                this._enter();
            },

            /** @private */
            _move(e, direction, cnt) {
                let li = this.li;
                if (li) {
                    do {
                        li = li[direction];
                    } while (li && (--cnt > 0 || !$util.isVisible(li) || li.getAttribute('inert')));
                    if (li) {
                        if (e.shiftKey) {
                            this.selectRange(li);
                        } else {
                            this.select(li, true);
                        }
                        $util.scrollIntoView(li, this.$refs._in);
                    }
                }
            },

            /** @private */
            selectRange(li) {
                if (li !== this.li) {
                    let i = this.option.indexOf(li);
                    let f = this.option.indexOf(this.li);
                    if (this.selectedOptions.indexOf(li) >= 0) {
                        const dir = f < i ? 1 : -1;
                        while (f != i) {
                            const li = this.option[f];
                            if ($util.isVisible(li) && !li.getAttribute('inert')) {
                                this.select(li);
                            }
                            f += dir;
                        }
                    } else {
                        const dir = f < i ? 1 : -1;
                        while (f != i) {
                            f += dir;
                            const li = this.option[f];
                            if ($util.isVisible(li) && !li.getAttribute('inert')) {
                                this.select(li);
                            }
                        }
                    }
                    this.li = li;
                }
            },

            /** @private */
            select(li, only) {
                if (this.li !== li || (this.multiple && (this.selectedOptions.length > 1 || !only))) {
                    if (!this.multiple || only) {
                        this._clear();
                        this.changed = true;
                    }
                    if (li) {
                        const i = this.selectedOptions.indexOf(li);
                        if (i < 0) {
                            this.selectedOptions.push(li);
                            li.setAttribute(selectAttr, 'true');
                        } else {
                            this.selectedOptions.splice(i, 1);
                            li.removeAttribute(selectAttr);
                        }
                        this.changed = true;
                    }
                    this.li = li;
                }
                if (this.active) {
                    this.active.classList.remove('_active');
                    this.active = null;
                }
            },

            /** @private */
            _clear() {
                for (let i = 0; i < this.selectedOptions.length; i++) {
                    this.selectedOptions[i].removeAttribute(selectAttr);
                }
                this.selectedOptions = [];
                this.li = null;
            },

            /** @private */
            _touchstart(e) {
                if (!this.disabled && !this.inert) {
                    const li = this.getOption(e.target);
                    if (li) {
                        this.active = li;
                        this.activeto = setTimeout(() => {
                            this.active.classList.add('_active');
                        }, 150);
                    }
                } else {
                    e.preventDefault();
                }
            },

            /** @private */
            _touchend(e) {
                if (this.active) {
                    this.active.classList.remove('_active');
                    this.active = null;
                    const li = this.getOption(e.target);
                    if (li) {
                        this.select(li);
                        this._enter();
                        e.preventDefault();
                    }
                    if (this.activeto) {
                        clearTimeout(this.activeto);
                        this.activeto = 0;
                    }
                }
            },

            /** @private */
            _touchmove(e) {
                if (this.active) {
                    const li = this.getOption(e.target);
                    if (li !== this.active) {
                        this.active.classList.remove('_active');
                        li.classList.add('_active');
                        this.active = li;
                        if (this.activeto) {
                            clearTimeout(this.activeto);
                            this.activeto = 0;
                        }
                    }
                }
            },

            /** @private */
            _scroll() {
                if (this.active) {
                    this.active.classList.remove('_active');
                    this.active = null;
                    if (this.activeto) {
                        clearTimeout(this.activeto);
                        this.activeto = 0;
                    }
                }
            },

            /** @private */
            _wheel(e) {
                if (this.disabled || this.inert) {
                    e.preventDefault();
                }
            },

            /** @private */
            _enter() {
                if (this.changed) {
                    this.changed = false;
                    let value = this.selectedOptions.map(el => el.getAttribute('data-value'));
                    if (!this.multiple) {
                        value = value.length ? value[0] : null;
                    }
                    this._set(value);
                    this.$emit('change', value);
                }
            },

            getOption(el) {
                while (el !== this.$el) {
                    if (el.getAttribute('role') == 'option') {
                        return el;
                    }
                    el = el.parentNode;
                }
            },

            isFiltered(option) {
                return this.filterexp ? this.filterexp.test(typeof option == 'object' ? option.label : option) : true;
            },

            /**
             * Custom validation, due to emulated component.
             * @private
             */
            validate() {
                let validity = null;
                if (this.error) {
                    validity = this.error;
                } else if (this.required && this.ivalue == null) {
                    validity = this.errorMissing ||
                        (this.multiple ? this.$locale.data.selMissingMul : this.$locale.data.selMissing);
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return this.valid;
            },

            getvalidity() {
                return this.validity;
            }
        }
    };
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._lb {
        position: relative;
        display: inline-flex;
        flex-direction: column;
        width: 24ch;
        height: 10em;
        .font-size(@fs6);
        font-weight: 400;
        color: @fg;
        background-color: @bg-field;
        outline: none;
        cursor: text;
        .listbox-default();

        ._fldi {
            flex: 0 0 auto;
            height: auto;
            padding: 4px 10px;
        }
        > ul {
            flex: 1 1 auto;
            margin: 0;
            padding: 0;
            overflow-x: hidden;
            overflow-y: auto;
            outline: 0;
            cursor: default;
            color: inherit;
            text-align: initial;
            vertical-align: top;
            white-space: nowrap;
            list-style-type: none;
            -webkit-overflow-scrolling: touch;
            -webkit-tap-highlight-color: transparent;
        }
        li {
            overflow: hidden;
            text-overflow: ellipsis;
            padding: 4px 10px;

            &._ind {
                padding-left: 20px;
            }
            &[aria-selected] {
                background: @bg-select;
                color: @fg-select;
            }
            &._active {
                background: fade(@fg, 8%);
            }
            &[inert] {
                font-weight: 600;
            }
        }
        &.compact li {
            padding-top: 0;
            padding-bottom: 0;
        }
        &.comfortable li {
            padding-top: 8px;
            padding-bottom: 8px;
        }
        &._filt > ul {
            border-top: 1px solid @border-field;
            /*
            height: calc(~'100% - ' unit(@fld-height / @font-base, rem));
            */
        }
        &._da li {
            opacity: .5;
        }
    }

    ._lebox         { .listbox-box(); }
    ._leframeless   { .listbox-frameless(); }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            ib-listbox(:data="list", v-model="value", height="160px")
            p(class="before-16") Value: 
                ib-field(id="value", v-model="value")
     </template>
    <script>
        {
            data() {
                return {
                    list: ['Eddard','Catelyn','Robb','Sansa','Brandon','Arya','Rickon'],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="multiple" name="Multiple">
    <template lang="pug">
        div
            ib-listbox(:data="list", v-model="value", height="160px", multiple)
            p(class="before-16") Value: 
                ib-field(id="value", readonly, v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    list: ['Eddard','Catelyn','Robb','Sansa','Brandon','Arya','Rickon'],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="filtered" name="Filtered">
    <template lang="pug">
        div
            ib-listbox(:data="list", v-model="value", height="160px", filtered)
            p(class="before-16") Value: 
                ib-field(id="value", v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    list: ['Eddard','Catelyn','Robb','Sansa','Brandon','Arya','Rickon'],
                    value: null
                };
            }
        }
    </script>
</ib-example>
